import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
/**
 * 送信者メール管理ページ プレゼンテーション
 */

import { Button } from "components/ui/Button";
import { Table } from "components/ui/Table/Table";
import type {
  CreateSenderEmailInput,
  SenderEmailItem,
  UpdateSenderEmailInput,
} from "hooks/use-sender-emails-api";
import {
  CheckCircle,
  Clock,
  Mail,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  XCircle,
} from "lucide-react";
import { type FC, useCallback, useState } from "react";
import { z } from "zod";

type Props = {
  senderEmails: SenderEmailItem[];
  loading: boolean;
  error: { message: string } | null;
  actionError: string | null;
  onCreate: (input: CreateSenderEmailInput) => Promise<void>;
  onUpdate: (id: string, input: UpdateSenderEmailInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onResendVerification: (id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isResending: boolean;
};

const createSchema = z.object({
  email: z.string().email("有効なメールアドレスを入力してください"),
  displayName: z.string().min(1, "表示名を入力してください").max(255),
});

const STATUS_CONFIG: Record<
  string,
  { label: string; icon: typeof Clock; tone: BadgeTone }
> = {
  pending: {
    label: "認証待ち",
    icon: Clock,
    tone: "soft-warning",
  },
  success: {
    label: "認証済み",
    icon: CheckCircle,
    tone: "soft-success",
  },
  failed: {
    label: "認証失敗",
    icon: XCircle,
    tone: "soft-error",
  },
};

/**
 * 送信者メール管理ページ
 * @param {Props} props コンポーネントProps
 * @return {JSX.Element} 送信者メール管理画面
 */
export const SenderEmailsPage: FC<Props> = ({
  senderEmails,
  loading,
  error,
  actionError,
  onCreate,
  onUpdate,
  onDelete,
  onResendVerification,
  onRefresh,
  isCreating,
  isUpdating,
  isDeleting,
  isResending,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );
  const [formEmail, setFormEmail] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleOpenAddModal = useCallback(() => {
    setFormEmail("");
    setFormDisplayName("");
    setFormErrors({});
    setShowAddModal(true);
  }, []);

  const handleCreate = useCallback(async () => {
    const result = createSchema.safeParse({
      email: formEmail,
      displayName: formDisplayName,
    });

    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        errors[issue.path[0] as string] = issue.message;
      }
      setFormErrors(errors);
      return;
    }

    try {
      await onCreate({ email: formEmail, displayName: formDisplayName });
      setShowAddModal(false);
    } catch (error) {
      // 409: duplicate email — show as form error, keep modal open
      if (
        error instanceof Error &&
        "status" in error &&
        (error as { status: number }).status === 409
      ) {
        setFormErrors({ email: error.message });
        return;
      }
      // Other errors handled by parent
    }
  }, [formEmail, formDisplayName, onCreate]);

  const handleSetDefault = useCallback(
    async (id: string) => {
      await onUpdate(id, { isDefault: true });
    },
    [onUpdate],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await onDelete(id);
      setShowDeleteConfirm(null);
    },
    [onDelete],
  );

  const isProcessing = isCreating || isUpdating || isDeleting || isResending;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error.message}</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">送信者メール管理</h1>
          <p className="text-sm text-base-content/60 mt-1">
            配信メールの送信者アドレスを管理します。登録後、SES認証メールが届きます。
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isProcessing}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleOpenAddModal}
            disabled={isProcessing}
          >
            <Plus className="w-4 h-4" />
            追加
          </Button>
        </div>
      </div>

      {actionError && (
        <div className="alert alert-error">
          <span>{actionError}</span>
        </div>
      )}

      {/* テーブル */}
      {senderEmails.length === 0 ? (
        <div className="card bg-base-100 shadow-md">
          <div className="card-body items-center text-center py-12">
            <Mail className="w-12 h-12 text-base-content/30" />
            <p className="text-base-content/60 mt-2">
              送信者メールアドレスが登録されていません
            </p>
            <div className="mt-4">
              <Button type="button" size="sm" onClick={handleOpenAddModal}>
                <Plus className="w-4 h-4" />
                最初の送信者を追加
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <Table.Root density="compact" zebra stickyHeader>
            <Table.Head sticky>
              <tr>
                <Table.HeaderCell>送信者名</Table.HeaderCell>
                <Table.HeaderCell>メールアドレス</Table.HeaderCell>
                <Table.HeaderCell>認証状態</Table.HeaderCell>
                <Table.HeaderCell>デフォルト</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </tr>
            </Table.Head>
            <Table.Body>
              {senderEmails.map(sender => {
                const statusConfig =
                  STATUS_CONFIG[sender.verificationStatus] ??
                  STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;

                return (
                  <Table.Row key={sender.id}>
                    <Table.Cell>
                      <span className="font-medium">{sender.displayName}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-base-content/70">
                        {sender.email}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <StatusBadge tone={statusConfig.tone}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </StatusBadge>
                    </Table.Cell>
                    <Table.Cell>
                      {sender.isDefault && (
                        <Star className="w-4 h-4 text-warning fill-warning" />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <div className="dropdown dropdown-end">
                        <Button tabIndex={0} variant="ghost" size="xs">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                        <ul className="dropdown-content z-50 menu p-2 shadow-lg bg-white rounded-box w-52 border border-slate-200">
                          {!sender.isDefault &&
                            sender.verificationStatus === "success" && (
                              <li>
                                <button
                                  type="button"
                                  onClick={() => handleSetDefault(sender.id)}
                                  disabled={isProcessing}
                                >
                                  <Star className="w-4 h-4" />
                                  デフォルトに設定
                                </button>
                              </li>
                            )}
                          {sender.verificationStatus !== "success" && (
                            <li>
                              <button
                                type="button"
                                onClick={() => onResendVerification(sender.id)}
                                disabled={isProcessing}
                              >
                                <RefreshCw className="w-4 h-4" />
                                認証メール再送
                              </button>
                            </li>
                          )}
                          <li>
                            <button
                              type="button"
                              className="text-error"
                              onClick={() => setShowDeleteConfirm(sender.id)}
                              disabled={isProcessing}
                            >
                              <Trash2 className="w-4 h-4" />
                              削除
                            </button>
                          </li>
                        </ul>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {/* 追加モーダル */}
      {showAddModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">送信者メールアドレスを追加</h3>
            <div className="py-4 space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    メールアドレス <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="email"
                  className={`input input-bordered input-sm w-full ${formErrors.email ? "input-error" : ""}`}
                  placeholder="sender@example.com"
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                />
                {formErrors.email && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {formErrors.email}
                    </span>
                  </label>
                )}
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">
                    表示名 <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  className={`input input-bordered input-sm w-full ${formErrors.displayName ? "input-error" : ""}`}
                  placeholder="山田 太郎"
                  value={formDisplayName}
                  onChange={e => setFormDisplayName(e.target.value)}
                />
                {formErrors.displayName && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {formErrors.displayName}
                    </span>
                  </label>
                )}
              </div>
              <div className="alert alert-info text-sm">
                <span>
                  登録後、入力したメールアドレスに認証メールが届きます。メール内のリンクをクリックして認証を完了してください。
                </span>
              </div>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAddModal(false)}
                disabled={isCreating}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
              >
                {isCreating ? <Loading size="sm" /> : "登録"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCreating && setShowAddModal(false)}
          />
        </div>
      )}

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">送信者メールを削除</h3>
            <p className="py-4">
              この送信者メールアドレスを削除しますか？SESの認証も取り消されます。
            </p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowDeleteConfirm(null)}
                disabled={isDeleting}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={isDeleting}
              >
                {isDeleting ? <Loading size="sm" /> : "削除"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isDeleting && setShowDeleteConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};
